/**
 * HotspotRow — W1.2 / B8.1
 *
 * Eine Zeile in der Hotspots-Liste.
 * Linke 3-px-Akzent-Kante per CSS-Klasse: .is-online / .is-warn / .is-offline
 * Ist ein <a>-Tag für Accessibility (Tab + Enter navigiert zu Detail).
 *
 * Design-Fix (Abnahme P2, hotspots.html): flache Tabellenzeile statt
 * Card-Layout — kein Avatar-Kreis, kein Rang, kein Guard-Badge, keine Tags-
 * /Heartbeat-Spalte (das waren die v1-"Spalten-Pillen", die der finale
 * Prototyp nicht kennt). Spalten jetzt 1:1 wie im Prototyp: Name → Status →
 * Type-Badge → 14-Tage-Trend → 24h/7d/30d → GB 24h → Chevron.
 */
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DemoTag } from '../../components/DemoTag'
import type { Hotspot, HotspotStatus } from './useHotspots'
import { Sparkline } from '../../components/Sparkline'
import { StatusPill } from '../../components/StatusPill'
import { deviceTypeLabel } from './deviceType'
import { isRawStatusSentinel } from '../../lib/hotspotStatusLabel'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import { formatHnt, STATUS_PILL_VARIANT, statusLabel } from './hotspotRowFormat'

type Props = {
  hotspot: Hotspot
  // Issue #39: nur in der Aktiv-Ansicht gesetzt (HotspotsList) — rendert den
  // Archivieren-Button. Button sitzt technisch im <a>-Row-Wrapper; der Klick
  // stoppt die Propagation, damit er nicht als Zeilen-Navigation durchschlaegt.
  onArchive?: (id: string | number) => void
  isArchiving?: boolean
}

/** Wandelt Hotspot-Adresse in kurze Darstellung um: erste 6 + letzte 4 Zeichen */
function shortAddress(address: string | null | undefined): string {
  if (!address) return '—'
  if (address.length <= 12) return address
  return `${address.slice(0, 6)}…${address.slice(-4)}`
}

const SPARKLINE_COLOR: Record<HotspotStatus, string> = {
  online: 'var(--primary)',
  warn: 'var(--warning)',
  offline: 'var(--danger)',
}

/**
 * Kurzes Typ-Badge (WiFi/CBRS/IoT), wie im Prototyp — bewusst OHNE die
 * ausfuehrliche "CBRS · Mobile"-Beschriftung von deviceTypeLabel(), die im
 * Prototyp so nicht vorkommt.
 */
function shortDeviceTypeLabel(dt: string | null | undefined, t: (key: string) => string): string {
  if (!dt) return ''
  // F-SW7-11 (AC2): rohe Sentinel-Werte ("not_found"/"unkown"/"unknown")
  // bleiben uebersetzt statt als rohes Kurz-Badge zu erscheinen.
  if (isRawStatusSentinel(dt)) return deviceTypeLabel(dt, t)
  const key = dt.toLowerCase()
  if (key === 'iot') return 'IoT'
  if (key === 'cbrs') return 'CBRS'
  if (key.startsWith('wifi')) return 'WiFi'
  return deviceTypeLabel(dt)
}

export function HotspotRow({ hotspot, onArchive, isArchiving }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation('hotspots')
  const isLegacy = hotspot.era === 'pre'

  function handleArchiveClick(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    onArchive?.(hotspot.id ?? '')
  }

  const rowClasses = ['list-row', `is-${hotspot.status}`, isLegacy ? 'is-legacy' : '']
    .filter(Boolean)
    .join(' ')

  // F-SW7-11 (AC1): namenlose/fehlerhafte Hotspot-Namen (z.B. name=null bei
  // device_type="not_found") zeigen eine gekürzte Adresse statt leerem Namen.
  const displayName = hotspotDisplayName(hotspot.name, hotspot.address, t('list.unknownHotspot'))

  const ariaLabel = `Hotspot ${displayName}, Status ${hotspot.status}`

  function handleClick(e: React.MouseEvent) {
    e.preventDefault()
    navigate({ to: '/hotspots/$id', params: { id: hotspot.id ?? '' } })
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      navigate({ to: '/hotspots/$id', params: { id: hotspot.id ?? '' } })
    }
  }

  // Prototyp (hotspots.html): eine Zeile pro Hotspot in fester Spaltenreihung
  // (Name → Status → Typ → 14-Tage-Trend → 24h/7d/30d → GB 24h). Layout kommt
  // aus hotspots-list.css (.hs-row-Klassen); hier nur die Zellen selbst, mit
  // hs-col-md/-sm fuer die Breakpoint-Sichtbarkeit und einer Mobile-Meta-Zeile
  // fuer den Card-Collapse <=640px.
  const mobileMeta = `${t('list.rewards.yesterday')} ${formatHnt(hotspot.hnt_24h)} · 7d ${formatHnt(hotspot.hnt_7d)} · 30d ${formatHnt(hotspot.hnt_30d)} HNT`

  return (
    <a
      href={`/hotspots/${hotspot.id}`}
      className={rowClasses}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Identity */}
      <div className="identity hs-cell-identity">
        <div className="identity-meta">
          <div className="identity-title">
            {displayName}
            <DemoTag style={{ marginLeft: 6 }} />
          </div>
          <div className="identity-sub">
            <span>{hotspot.city?.name ?? ''}</span>
            <span>·</span>
            <span className="addr-chip">{shortAddress(hotspot.address)}</span>
          </div>
        </div>
      </div>

      {/* Status + Online Meter */}
      <div
        className="hs-cell-status"
        style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}
      >
        <StatusPill
          variant={STATUS_PILL_VARIANT[hotspot.status ?? 'offline']}
          label={statusLabel(hotspot.status ?? 'offline', hotspot.online_pct ?? 0, t)}
          className="status-pill"
        />
        <div className="meter" style={{ width: '100%' }}>
          <div
            className={`meter-fill ${
              hotspot.status === 'online'
                ? 'tone-success'
                : hotspot.status === 'warn'
                  ? 'tone-warning'
                  : 'tone-danger'
            }`}
            style={{ width: `${hotspot.online_pct ?? 0}%` }}
          />
        </div>
      </div>

      {/* Type-Badge — schlicht (Prototyp: WiFi/CBRS/IoT, keine Zusatzzeile) */}
      <div className="hs-cell-type hs-col-md">
        <span className="pill">{shortDeviceTypeLabel(hotspot.device_type, t)}</span>
      </div>

      {/* 14-Tage-Trend (Sparkline) */}
      <div className="hs-cell-spark hs-col-md" style={{ height: '32px', alignItems: 'center' }}>
        {hotspot.sparkline_data && hotspot.sparkline_data.length >= 2 ? (
          <Sparkline
            values={hotspot.sparkline_data}
            color={SPARKLINE_COLOR[hotspot.status ?? 'offline']}
            width={120}
            height={32}
          />
        ) : (
          <div className="sparkline-placeholder" aria-hidden="true" />
        )}
      </div>

      {/* Reward-Werte */}
      <div className="reward-cols hs-col-sm">
        <span className="num-sm" aria-label={`${t('list.rewards.yesterday')} Rewards: ${hotspot.hnt_24h ?? '—'} HNT`}>
          <span className="label-xs muted">{t('list.rewards.yesterday')}</span>
          <span style={{ marginLeft: 4 }}>{formatHnt(hotspot.hnt_24h)}</span>
        </span>
        <span className="num-sm" aria-label={`7d Rewards: ${hotspot.hnt_7d ?? '—'} HNT`}>
          <span className="label-xs muted">7d</span>
          <span style={{ marginLeft: 4 }}>{formatHnt(hotspot.hnt_7d)}</span>
        </span>
        <span className="num-sm" aria-label={`30d Rewards: ${hotspot.hnt_30d ?? '—'} HNT`}>
          <span className="label-xs muted">30d</span>
          <span style={{ marginLeft: 4 }}>{formatHnt(hotspot.hnt_30d)}</span>
        </span>
      </div>

      {/* GB 24h — api/v2 hotspots liefert (noch) kein Mobile-Datenvolumen-Feld
          fuer die Liste; Spalte matcht den Prototyp strukturell, zeigt aber
          ehrlich "—" statt erfundener Werte (Tech-Debt: BE-Feld nachziehen). */}
      <div className="hs-cell-gb hs-col-md num">
        <span className="label-xs muted">GB 24h</span>
        <span style={{ marginLeft: 4 }}>—</span>
      </div>

      {/* Card-Collapse <=640px: Zusammenfassung statt der einzelnen Spalten (Prototyp .hs-mobile-meta). */}
      <div className="hs-mobile-meta">{mobileMeta}</div>

      {/* Issue #39: Archivieren-Button — nur in der Aktiv-Ansicht (onArchive
          gesetzt). Blendet den Hotspot aus und gibt seinen Kontingent-Slot frei. */}
      {onArchive && (
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          title={t('list.archive.archiveBtn')}
          aria-label={t('list.archive.archiveBtn')}
          onClick={handleArchiveClick}
          disabled={isArchiving}
          data-testid={`hotspot-archive-${hotspot.id}`}
        >
          &times;
        </button>
      )}

      {/* Chevron */}
      <div
        className="hs-col-chevron"
        style={{ color: 'var(--text-subtle)', display: 'grid', placeItems: 'center' }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 4l4 4-4 4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </a>
  )
}
